import type { ReactNode } from 'react';

/**
 * The console's dialog shell, factored out here because the points screens need three of them
 * and the existing ones (`RoomModal`, `TemplateModal`) each carry their own copy of this markup.
 * Same shape, same tokens — this is not a new pattern, just the one already in use with a name.
 */
export function Dialog({
  title,
  subtitle,
  onClose,
  width = 'max-w-lg',
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  width?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className={`flex max-h-[90vh] w-full ${width} flex-col rounded-xl bg-white shadow-xl`}>
        <header className="flex items-start justify-between gap-4 border-b border-ink-800/10 p-6">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold text-ink-950">{title}</h3>
            {subtitle ? <p className="mt-0.5 text-xs text-ink-800/60">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-lg leading-none text-ink-800/40 hover:text-ink-950"
          >
            ×
          </button>
        </header>
        <div className="flex-1 overflow-auto p-6">{children}</div>
        {footer ? (
          <footer className="flex gap-2 border-t border-ink-800/10 p-6">{footer}</footer>
        ) : null}
      </div>
    </div>
  );
}
